import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGlobe, FiFileText, FiCheck, FiLoader, FiZap
} from 'react-icons/fi';
import {
  FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok
} from 'react-icons/fa';
import Styles from './BrandKnowledgeAnimation.module.scss';

// Data source configurations with brand colors
const DATA_SOURCES = [
  { id: 'website', icon: FiGlobe, label: 'Website', color: '#00D4C5', glowColor: 'rgba(0, 212, 197, 0.6)', angle: 270 },
  { id: 'twitter', icon: FaTwitter, label: 'Twitter', color: '#1DA1F2', glowColor: 'rgba(29, 161, 242, 0.6)', angle: 324 },
  { id: 'linkedin', icon: FaLinkedin, label: 'LinkedIn', color: '#0A66C2', glowColor: 'rgba(10, 102, 194, 0.6)', angle: 18 },
  { id: 'instagram', icon: FaInstagram, label: 'Instagram', color: '#E4405F', glowColor: 'rgba(228, 64, 95, 0.6)', angle: 72 },
  { id: 'youtube', icon: FaYoutube, label: 'YouTube', color: '#FF0000', glowColor: 'rgba(255, 0, 0, 0.6)', angle: 126 },
  { id: 'tiktok', icon: FaTiktok, label: 'TikTok', color: '#00F2EA', glowColor: 'rgba(0, 242, 234, 0.6)', angle: 180 },
  { id: 'files', icon: FiFileText, label: 'Documents', color: '#8B5CF6', glowColor: 'rgba(139, 92, 246, 0.6)', angle: 234 },
];

// Bezier curve path from source to center
const createCurvePath = (startX, startY, endX, endY) => {
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  const controlOffset = 40;

  // Create a slight curve
  const angle = Math.atan2(endY - startY, endX - startX);
  const perpAngle = angle + Math.PI / 2;
  const ctrlX = midX + Math.cos(perpAngle) * controlOffset;
  const ctrlY = midY + Math.sin(perpAngle) * controlOffset;

  return `M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`;
};

// Animated data particle
const DataParticle = ({ path, color, delay, duration }) => {
  return (
    <motion.circle
      r={3}
      fill={color}
      filter={`drop-shadow(0 0 8px ${color})`}
      initial={{ offsetDistance: '0%', opacity: 0 }}
      animate={{
        offsetDistance: '100%',
        opacity: [0, 1, 1, 0.8, 0]
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "linear",
      }}
      style={{ offsetPath: `path('${path}')` }}
    />
  );
};

// Glowing connection beam
const ConnectionBeam = ({ startX, startY, endX, endY, color, isActive, progress }) => {
  const path = createCurvePath(startX, startY, endX, endY);

  return (
    <g>
      {/* Base path (dim) */}
      <motion.path
        d={path}
        fill="none"
        stroke="rgba(0, 167, 183, 0.1)"
        strokeWidth={2}
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      />

      {/* Active glow path */}
      {isActive && (
        <>
          <motion.path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            filter={`drop-shadow(0 0 6px ${color})`}
            initial={{ pathLength: 0, opacity: 0.3 }}
            animate={{
              pathLength: progress || 1,
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{
              pathLength: { duration: 0.5 },
              opacity: { duration: 2, repeat: Infinity }
            }}
          />
          {/* Data particles */}
          {[0, 0.5, 1, 1.5, 2].map((d, i) => (
            <DataParticle
              key={i}
              path={path}
              color={color}
              delay={d}
              duration={2}
            />
          ))}
        </>
      )}

      {/* Complete state - solid line */}
      {progress === 1 && !isActive && (
        <motion.path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          opacity={0.4}
          filter={`drop-shadow(0 0 4px ${color})`}
        />
      )}
    </g>
  );
};

// Source node with status
const SourceNode = ({ source, status, x, y, onComplete }) => {
  const Icon = source.icon;
  const isActive = status === 'loading';
  const isComplete = status === 'complete';
  const isPending = status === 'pending';
  const isInactive = !status; // No status = dormant/unused source

  return (
    <motion.g
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: (source.angle / 360) * 0.5
      }}
    >
      {/* Outer pulse ring when active */}
      {isActive && (
        <>
          <motion.circle
            cx={x}
            cy={y}
            r={32}
            fill="none"
            stroke={source.color}
            strokeWidth={1}
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
          />
          <motion.circle
            cx={x}
            cy={y}
            r={32}
            fill="none"
            stroke={source.color}
            strokeWidth={1}
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
          />
        </>
      )}

      {/* Glow background */}
      <motion.circle
        cx={x}
        cy={y}
        r={26}
        fill={isActive || isComplete ? source.color : 'transparent'}
        opacity={isActive ? 0.15 : isComplete ? 0.1 : 0}
        filter={`blur(8px)`}
      />

      {/* Main node - always visible with source color */}
      <motion.circle
        cx={x}
        cy={y}
        r={24}
        fill="#FFFFFF"
        stroke={isComplete ? '#00D4C5' : isActive ? source.color : isInactive ? 'rgba(156, 163, 175, 0.4)' : source.color}
        strokeWidth={isActive || isComplete ? 2 : 1.5}
        opacity={isInactive ? 0.6 : 1}
        filter={isActive ? `drop-shadow(0 4px 12px ${source.glowColor})` : 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))'}
        animate={isActive ? {
          filter: [
            `drop-shadow(0 4px 8px ${source.glowColor})`,
            `drop-shadow(0 4px 16px ${source.glowColor})`,
            `drop-shadow(0 4px 8px ${source.glowColor})`
          ]
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity }}
      />

      {/* Icon container - ALWAYS show source icon */}
      <foreignObject x={x - 12} y={y - 12} width={24} height={24}>
        <div className={Styles.nodeIcon} style={{
          color: isComplete ? '#00D4C5' : isActive ? source.color : isInactive ? '#9CA3AF' : source.color,
          opacity: isInactive ? 0.7 : 1
        }}>
          <Icon size={16} />
        </div>
      </foreignObject>

      {/* Status badge overlay */}
      {(isActive || isComplete) && (
        <foreignObject x={x + 10} y={y - 20} width={16} height={16}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: isComplete ? '#00D4C5' : source.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {isComplete ? (
              <FiCheck size={10} color="#fff" />
            ) : (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <FiLoader size={10} color="#fff" />
              </motion.div>
            )}
          </motion.div>
        </foreignObject>
      )}

      {/* Label */}
      <motion.text
        x={x}
        y={y + 42}
        textAnchor="middle"
        fill={isActive ? source.color : isComplete ? '#00D4C5' : isInactive ? '#9CA3AF' : '#6B7280'}
        fontSize={10}
        fontWeight={500}
        fontFamily="'DM Sans', sans-serif"
        initial={{ opacity: 0 }}
        animate={{ opacity: isInactive ? 0.6 : 1 }}
        transition={{ delay: 0.5 }}
      >
        {source.label}
      </motion.text>

      {/* Status indicator */}
      {isActive && (
        <motion.text
          x={x}
          y={y + 54}
          textAnchor="middle"
          fill={source.color}
          fontSize={8}
          fontFamily="'DM Sans', sans-serif"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          Analyzing...
        </motion.text>
      )}
    </motion.g>
  );
};

// Central hub with pulsing rings
const CentralHub = ({ isProcessing, completedCount, totalCount }) => {
  const progress = totalCount > 0 ? completedCount / totalCount : 0;

  return (
    <g>
      {/* Outer orbital rings */}
      {[120, 100, 80].map((r, i) => (
        <motion.circle
          key={r}
          cx={200}
          cy={200}
          r={r}
          fill="none"
          stroke="rgba(0, 167, 183, 0.08)"
          strokeWidth={1}
          strokeDasharray={`${Math.PI * r * 0.1} ${Math.PI * r * 0.05}`}
          initial={{ rotate: 0 }}
          animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
          transition={{ duration: 60 + i * 20, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: '200px 200px' }}
        />
      ))}

      {/* Processing pulse rings */}
      {isProcessing && (
        <>
          <motion.circle
            cx={200}
            cy={200}
            r={50}
            fill="none"
            stroke="url(#hubGradient)"
            strokeWidth={2}
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          />
          <motion.circle
            cx={200}
            cy={200}
            r={50}
            fill="none"
            stroke="url(#hubGradient)"
            strokeWidth={1}
            initial={{ scale: 1, opacity: 0.4 }}
            animate={{ scale: 2.5, opacity: 0 }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
          />
        </>
      )}

      {/* Progress ring */}
      <motion.circle
        cx={200}
        cy={200}
        r={46}
        fill="none"
        stroke="rgba(0, 212, 197, 0.3)"
        strokeWidth={4}
        strokeLinecap="round"
        strokeDasharray={`${Math.PI * 92}`}
        strokeDashoffset={Math.PI * 92 * (1 - progress)}
        transform="rotate(-90 200 200)"
        filter="drop-shadow(0 0 8px rgba(0, 212, 197, 0.5))"
      />

      {/* Main hub glow */}
      <motion.circle
        cx={200}
        cy={200}
        r={44}
        fill="url(#hubRadialGradient)"
        filter="drop-shadow(0 0 20px rgba(0, 167, 183, 0.4))"
        animate={isProcessing ? {
          filter: [
            'drop-shadow(0 0 15px rgba(0, 167, 183, 0.3))',
            'drop-shadow(0 0 25px rgba(0, 167, 183, 0.5))',
            'drop-shadow(0 0 15px rgba(0, 167, 183, 0.3))'
          ]
        } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      />

      {/* Hub border */}
      <motion.circle
        cx={200}
        cy={200}
        r={44}
        fill="none"
        stroke="url(#hubBorderGradient)"
        strokeWidth={2}
      />

      {/* Center icon */}
      <foreignObject x={184} y={184} width={32} height={32}>
        <div className={Styles.hubIcon}>
          <motion.div
            animate={isProcessing ? { rotate: [0, 360] } : {}}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <FiZap size={20} />
          </motion.div>
        </div>
      </foreignObject>
    </g>
  );
};

// Status message overlay
const StatusMessage = ({ message, subMessage }) => (
  <motion.div
    className={Styles.statusOverlay}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3 }}
  >
    <motion.div
      className={Styles.statusTitle}
      animate={{ opacity: [0.7, 1, 0.7] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      {message}
    </motion.div>
    {subMessage && (
      <div className={Styles.statusSubtitle}>{subMessage}</div>
    )}
  </motion.div>
);

// Activity log integrated into animation
const ActivityLog = ({ activities }) => (
  <div className={Styles.activityLog}>
    <AnimatePresence>
      {activities.slice(-4).map((activity, i) => (
        <motion.div
          key={activity.id}
          className={Styles.activityItem}
          initial={{ opacity: 0, x: -20, height: 0 }}
          animate={{ opacity: 1, x: 0, height: 'auto' }}
          exit={{ opacity: 0, x: 20, height: 0 }}
          transition={{ duration: 0.3 }}
          style={{ color: activity.color }}
        >
          <span className={Styles.activityDot} style={{ backgroundColor: activity.color }} />
          <span className={Styles.activityText}>{activity.message}</span>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
);

// Main component
const BrandKnowledgeAnimation = ({
  sourceStatuses = {},
  onSourceClick,
  title = "Building Brand Intelligence",
  activities = []
}) => {
  const [internalActivities, setInternalActivities] = useState([]);
  const centerX = 200;
  const centerY = 200;
  const radius = 140;

  // Calculate node positions
  const nodePositions = useMemo(() => {
    return DATA_SOURCES.reduce((acc, source) => {
      const angleRad = (source.angle - 90) * Math.PI / 180;
      acc[source.id] = {
        x: centerX + radius * Math.cos(angleRad),
        y: centerY + radius * Math.sin(angleRad)
      };
      return acc;
    }, {});
  }, []);

  // Track status changes for activity log
  useEffect(() => {
    Object.entries(sourceStatuses).forEach(([sourceId, status]) => {
      const source = DATA_SOURCES.find(s => s.id === sourceId);
      if (!source) return;

      const existingActivity = internalActivities.find(
        a => a.sourceId === sourceId && a.status === status
      );

      if (!existingActivity) {
        const message = status === 'loading'
          ? `Analyzing ${source.label}...`
          : status === 'complete'
          ? `${source.label} complete`
          : null;

        if (message) {
          setInternalActivities(prev => [
            ...prev,
            {
              id: `${sourceId}-${status}-${Date.now()}`,
              sourceId,
              status,
              message,
              color: source.color
            }
          ]);
        }
      }
    });
  }, [sourceStatuses]);

  const isProcessing = Object.values(sourceStatuses).includes('loading');
  const completedCount = Object.values(sourceStatuses).filter(s => s === 'complete').length;
  const totalCount = Object.keys(sourceStatuses).length;

  const statusMessage = isProcessing
    ? "Extracting brand intelligence..."
    : completedCount === totalCount && totalCount > 0
    ? "Analysis complete!"
    : "Preparing analysis...";

  return (
    <div className={Styles.container}>
      <svg viewBox="0 0 400 400" className={Styles.svg}>
        {/* Gradient definitions */}
        <defs>
          <radialGradient id="hubRadialGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00A7B7" />
            <stop offset="70%" stopColor="#006D75" />
            <stop offset="100%" stopColor="#0D1117" />
          </radialGradient>

          <linearGradient id="hubGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00D4C5" />
            <stop offset="100%" stopColor="#00A7B7" />
          </linearGradient>

          <linearGradient id="hubBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(0, 212, 197, 0.8)" />
            <stop offset="50%" stopColor="rgba(0, 167, 183, 0.6)" />
            <stop offset="100%" stopColor="rgba(0, 212, 197, 0.8)" />
          </linearGradient>

          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background grid pattern */}
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(0, 167, 183, 0.04)" strokeWidth="0.5"/>
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* Connection beams */}
        {DATA_SOURCES.map(source => {
          const pos = nodePositions[source.id];
          const status = sourceStatuses[source.id];
          return (
            <ConnectionBeam
              key={source.id}
              startX={pos.x}
              startY={pos.y}
              endX={centerX}
              endY={centerY}
              color={source.color}
              isActive={status === 'loading'}
              progress={status === 'complete' ? 1 : 0}
            />
          );
        })}

        {/* Central hub */}
        <CentralHub
          isProcessing={isProcessing}
          completedCount={completedCount}
          totalCount={totalCount}
        />

        {/* Source nodes */}
        {DATA_SOURCES.map(source => {
          const pos = nodePositions[source.id];
          return (
            <SourceNode
              key={source.id}
              source={source}
              status={sourceStatuses[source.id]}
              x={pos.x}
              y={pos.y}
            />
          );
        })}
      </svg>

      {/* Status message */}
      <StatusMessage
        message={statusMessage}
        subMessage={totalCount > 0 ? `${completedCount} of ${totalCount} sources processed` : null}
      />

      {/* Activity log */}
      <ActivityLog activities={internalActivities} />
    </div>
  );
};

export default BrandKnowledgeAnimation;
